import {
  CalendarSongListTypes,
  IntervalType,
  SongList,
  SongListEntry,
  SongListType,
} from '../../../../../common/src/model.ts';
import { hasInterval, normalizeHymnOfTheMonthSongs } from '../../../../../common/src/model_util.ts';

// Pure row logic behind the planner's cells and its row inserts and deletes (issue #3609).
// A hymn-of-the-month list holds at most one song, and its row means nothing: readers take its single song
// whatever its row, and writers always store it at row 1.

/**
 * Returns `songList` with its entries shifted by `offset` from `rowIndex` down (a negative offset also removes the
 * entries at `rowIndex`), or `undefined` when row inserts and deletes do not apply to it. Hymn-of-the-month lists
 * never shift: a template row inserted above row 1 once moved them to row 3, where the planner could not see them.
 */
export function shiftSongListRows(
  songList: SongList,
  { rowIndex, offset, intervalTypes }: { rowIndex: number; offset: number; intervalTypes?: IntervalType[] },
): SongList | undefined {
  if (
    songList.type === SongListType.HymnOfTheMonth ||
    CalendarSongListTypes.includes(songList.type) ||
    (intervalTypes && hasInterval(songList) && !intervalTypes.includes(songList.interval.type))
  ) {
    return undefined;
  }
  return {
    ...songList,
    songs: songList.songs
      .filter(({ row }) => offset > 0 || row !== rowIndex) // remove rows if offset negative
      .map((song) => ({ ...song, row: song.row >= rowIndex ? song.row + offset : song.row })),
  };
}

/**
 * The entry a planner cell at `row` shows: for a hymn-of-the-month list, its single song at any row (the row 1
 * entry, else the lowest-row one). Returns the stored entry itself, never a copy, so a cell's memoized callbacks
 * stay stable across renders; its row may not be 1, and writes go through replaceSongListEntry, which normalizes.
 */
export function findSongListEntry(songList: SongList, row: number): SongListEntry | undefined {
  if (songList.type === SongListType.HymnOfTheMonth) {
    const { songs } = songList;
    return (
      songs.find((song) => song.row === 1) ??
      songs.reduce<SongListEntry | undefined>(
        (lowest, song) => (lowest && lowest.row <= song.row ? lowest : song),
        undefined,
      )
    );
  }
  return songList.songs.find((song) => song.row === row);
}

/** `songList` with the cell at `row` set to `entry`: a hymn-of-the-month list becomes that one entry, at row 1. */
export function replaceSongListEntry(songList: SongList, row: number, entry: SongListEntry): SongList {
  if (songList.type === SongListType.HymnOfTheMonth) {
    return { ...songList, songs: normalizeHymnOfTheMonthSongs([entry]) };
  }
  return {
    ...songList,
    songs: [...songList.songs.filter((song) => song.row !== row), entry].sort((lhs, rhs) => lhs.row - rhs.row),
  };
}

/** `songList` with the cell at `row` cleared: a hymn-of-the-month list loses its song whatever its row. */
export function removeSongListEntry(songList: SongList, row: number): SongList {
  if (songList.type === SongListType.HymnOfTheMonth) {
    return { ...songList, songs: [] };
  }
  return { ...songList, songs: songList.songs.filter((song) => song.row !== row) };
}
